import {
    Component,
    ErrorInfo,
    ReactNode,
    useEffect,
    useRef,
    useState
} from 'react';
import {useRouteError} from 'react-router';
import {
    Container,
    Card,
    CardBody,
    CardHeader,
    Alert,
    Button,
    Input,
    Label
} from 'reactstrap';
import {jwtDecode} from 'jwt-decode';
import {getLocalAccessToken} from '../lib/localAuth';
import {
    currentUrlWithoutCacheBust,
    isRepeatChunkFailure,
    reloadBypassingCache
} from '../lib/chunkReloadGuard';

interface Props {
    children: ReactNode;
}

/**
 * Detect the "stale chunk" failure: a tab opened before a deploy tries to lazy-load a
 * code-split module whose hashed filename no longer exists on the server (the new build
 * replaced it). It surfaces as a dynamic-import failure with browser-specific wording, so
 * we match all of them rather than the exact Chrome string.
 */
export function isChunkLoadError(error: Error | null): boolean {
    if (!error) {
        return false;
    }
    const haystack = `${error.name} ${error.message}`;
    return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css|chunkloaderror/i.test(
        haystack
    );
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

/** Assemble a human-readable diagnostic block for support tickets. */
export function buildErrorReport(
    error: Error | null,
    componentStack?: string | null
): string {
    const safe = (fn: () => string): string => {
        try {
            return fn();
        } catch {
            return 'unavailable';
        }
    };

    // User/tenant come from the local JWT (sessionStorage) rather than React state,
    // because the React tree below the failure may be broken. Decoded defensively.
    const claims = safe(() => {
        const token = getLocalAccessToken();
        if (!token) {
            return '';
        }
        return JSON.stringify(jwtDecode<Record<string, unknown>>(token));
    });

    const decoded: Record<string, unknown> = (() => {
        try {
            return claims ? JSON.parse(claims) : {};
        } catch {
            return {};
        }
    })();

    const user = safe(() => {
        const email =
            (decoded.upn as string) ??
            (decoded.email as string) ??
            (decoded.preferred_username as string);
        if (!email) {
            return 'Not signed in / unavailable';
        }
        const fullName = `${(decoded.given_name as string) ?? ''} ${
            (decoded.family_name as string) ?? ''
        }`.trim();
        const oid = decoded.oid ? `  [oid: ${decoded.oid}]` : '';
        return `${fullName || email} (${email})${oid}`;
    });

    const tenant = safe(() => (decoded.tid as string) ?? 'unavailable');
    const page = safe(
        () =>
            `${window.location.pathname}${window.location.search}${window.location.hash}`
    );
    const fullUrl = safe(() => window.location.href);
    const mode = safe(() => import.meta.env.MODE);
    const title = safe(() => document.title);

    const browser = safe(() => navigator.userAgent);
    const language = safe(() => navigator.language);
    const viewport = safe(() => `${window.innerWidth}x${window.innerHeight}`);
    const screenSize = safe(
        () => `${window.screen.width}x${window.screen.height}`
    );
    const online = safe(() => String(navigator.onLine));
    const time = safe(() => new Date().toISOString());

    return [
        '=== Theta Assist Error Report ===',
        `Time:       ${time}`,
        `App mode:   ${mode}`,
        `Page:       ${page}`,
        `Full URL:   ${fullUrl}`,
        `Title:      ${title}`,
        `User:       ${user}`,
        `Tenant:     ${tenant}`,
        '',
        '--- Error ---',
        `${error?.name ?? 'Error'}: ${error?.message ?? '(no message)'}`,
        error?.stack ?? '(no stack trace)',
        '',
        '--- Component stack ---',
        componentStack?.trim() ?? '(no component stack)',
        '',
        '--- Browser ---',
        `User agent: ${browser}`,
        `Language:   ${language}`,
        `Viewport:   ${viewport}   Screen: ${screenSize}`,
        `Online:     ${online}`
    ].join('\n');
}

/**
 * Recovery screen for the stale-chunk case (see isChunkLoadError). This is almost never a
 * real fault — the tab was open across a release, or was opened while the release was
 * still landing on the server — so we show a friendly
 * "update available" message instead of the scary diagnostic report. Reload is manual (a
 * single button) so we never discard unsaved work on the page without the user's say-so.
 *
 * Reload goes through reloadBypassingCache, which stamps the attempt in sessionStorage and
 * cache-busts the URL. If this screen shows again shortly after that stamp, the reload
 * didn't help (stale cached HTML or a mid-deploy 404), so we say so and point the user at
 * the two things that reliably work: wait a moment, or open a fresh tab.
 */
function ChunkReloadFallback(): JSX.Element {
    // Evaluated once on mount: the stamp is written by the button below, and re-reading
    // it after a click would flip the copy mid-navigation.
    const [isRepeat] = useState(isRepeatChunkFailure);

    const handleOpenNewTab = (): void => {
        window.open(currentUrlWithoutCacheBust(), '_blank', 'noopener');
    };

    if (isRepeat) {
        return (
            <Container className="py-5" style={{maxWidth: 560}}>
                <Card className="shadow-sm">
                    <CardHeader className="bg-primary text-white">
                        <h5 className="mb-0">Still finishing the update</h5>
                    </CardHeader>
                    <CardBody className="text-center">
                        <p className="text-muted mb-2">
                            Theta Assist was just updated and this tab still
                            couldn&apos;t load the latest version. This normally
                            clears within a few minutes of a release.
                        </p>
                        <p className="text-muted mb-3">
                            Open Theta Assist in a new tab, or wait a moment and
                            try again.
                        </p>
                        <div className="d-flex justify-content-center gap-2">
                            <Button color="primary" onClick={handleOpenNewTab}>
                                Open in a new tab
                            </Button>
                            <Button
                                color="secondary"
                                outline
                                onClick={reloadBypassingCache}
                            >
                                Try again
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="py-5" style={{maxWidth: 560}}>
            <Card className="shadow-sm">
                <CardHeader className="bg-primary text-white">
                    <h5 className="mb-0">A new version is available</h5>
                </CardHeader>
                <CardBody className="text-center">
                    <p className="text-muted mb-3">
                        Theta Assist has just been updated and part of this page
                        couldn&apos;t load. Reload to pick up the latest
                        version.
                    </p>
                    <Button color="primary" onClick={reloadBypassingCache}>
                        Reload
                    </Button>
                </CardBody>
            </Card>
        </Container>
    );
}

/**
 * Generic recovery screen — a copy-ready diagnostic report (route, browser, user, error +
 * component stack) to attach to a support ticket. Reads context from globals so it works
 * even when the React tree below the failure is broken. Always its own component (never
 * reached via an early return) so its hooks run unconditionally per React's rules.
 */
function SupportTicketFallback({
    error,
    componentStack
}: {
    error: Error | null;
    componentStack?: string | null;
}): JSX.Element {
    const report = buildErrorReport(error, componentStack);
    const reportRef = useRef<HTMLInputElement>(null);
    const [copied, setCopied] = useState(false);
    const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (copyResetTimer.current) {
                clearTimeout(copyResetTimer.current);
            }
        },
        []
    );

    const handleCopy = async (): Promise<void> => {
        let ok = false;
        try {
            await navigator.clipboard.writeText(report);
            ok = true;
        } catch {
            // Fallback for non-secure contexts where the Clipboard API is unavailable.
            const el = reportRef.current;
            if (el) {
                el.removeAttribute('readonly');
                el.select();
                try {
                    ok = document.execCommand('copy');
                } catch {
                    ok = false;
                }
                el.setAttribute('readonly', 'true');
                window.getSelection()?.removeAllRanges();
            }
        }
        if (ok) {
            setCopied(true);
            if (copyResetTimer.current) {
                clearTimeout(copyResetTimer.current);
            }
            copyResetTimer.current = setTimeout(() => setCopied(false), 2500);
        }
    };

    const handleReload = (): void => window.location.reload();
    const handleGoHome = (): void => window.location.assign('/');

    return (
        <Container className="py-4" style={{maxWidth: 760}}>
            <Card className="shadow-sm">
                <CardHeader className="bg-danger text-white">
                    <h5 className="mb-0">Something went wrong</h5>
                </CardHeader>
                <CardBody>
                    <p className="text-muted">
                        The application hit an unexpected error and can&apos;t
                        continue on this screen. Any unsaved changes on this
                        page may have been lost.
                    </p>

                    <Alert color="info" className="mb-3">
                        <strong>Raising a support ticket?</strong> Please copy
                        the diagnostic details below and paste them into your
                        ticket - they help us pinpoint and fix the issue
                        quickly.
                    </Alert>

                    <Label for="error-report" className="fw-semibold">
                        Diagnostic details
                    </Label>
                    <Input
                        id="error-report"
                        type="textarea"
                        readOnly
                        innerRef={reportRef}
                        value={report}
                        rows={14}
                        style={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            whiteSpace: 'pre'
                        }}
                        onFocus={e => e.currentTarget.select()}
                    />

                    <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
                        <Button color="primary" onClick={handleCopy}>
                            {copied ? 'Copied ✓' : 'Copy details'}
                        </Button>
                        <Button
                            color="secondary"
                            outline
                            onClick={handleReload}
                        >
                            Reload page
                        </Button>
                        <Button
                            color="secondary"
                            outline
                            onClick={handleGoHome}
                        >
                            Go to home
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </Container>
    );
}

/**
 * Recovery screen dispatcher shared by BOTH the class ErrorBoundary (errors outside the
 * router) and RouteErrorBoundary (router-caught render/loader errors). Picks the friendly
 * reload prompt for the benign stale-chunk case and the support-ticket diagnostic for
 * everything else. Holds no hooks itself so the conditional branch is safe.
 */
function ErrorFallback({
    error,
    componentStack
}: {
    error: Error | null;
    componentStack?: string | null;
}): JSX.Element {
    if (isChunkLoadError(error)) {
        return <ChunkReloadFallback />;
    }
    return (
        <SupportTicketFallback error={error} componentStack={componentStack} />
    );
}

/**
 * Route-level error boundary for the data router. With `createBrowserRouter`, react-router
 * catches render/loader errors inside route elements and renders the route's `errorElement`
 * (falling back to its own bare-bones screen if none is set). Wire this as the catch-all
 * route's `errorElement` so router-caught errors show the same recovery UI as the class
 * boundary.
 */
export function RouteErrorBoundary(): JSX.Element {
    const routeError = useRouteError();
    console.error('RouteErrorBoundary caught an error:', routeError);
    const error =
        routeError instanceof Error
            ? routeError
            : new Error(
                  typeof routeError === 'string'
                      ? routeError
                      : 'Unexpected application error'
              );
    return <ErrorFallback error={error} />;
}

/**
 * App-level error boundary. Catches render-time errors anywhere below it and shows the
 * recovery screen. Must be a class component - React only supports error boundaries via
 * getDerivedStateFromError / componentDidCatch.
 *
 * NOTE: under the data router this only catches errors OUTSIDE route rendering (e.g. the
 * provider tree). Errors thrown while rendering a route element are caught by react-router
 * and routed to RouteErrorBoundary (the catch-all route's `errorElement`).
 */
export class ErrorBoundary extends Component<Props, State> {
    state: State = {hasError: false, error: null, errorInfo: null};

    static getDerivedStateFromError(error: Error): Partial<State> {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Keep the component stack for the diagnostic report and log for the console/telemetry.
        this.setState({errorInfo});
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    render(): ReactNode {
        if (!this.state.hasError) {
            return this.props.children;
        }
        return (
            <ErrorFallback
                error={this.state.error}
                componentStack={this.state.errorInfo?.componentStack}
            />
        );
    }
}

export default ErrorBoundary;
